import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { LineChart, Line, ResponsiveContainer, Tooltip, XAxis, YAxis, CartesianGrid } from "recharts";
import { api, config } from "../lib/api.js";
import { PageHeader } from "../components/PageHeader.jsx";
import { EmptyState } from "../components/EmptyState.jsx";

export function Posts() {
  const { data, isLoading, error } = useQuery({
    queryKey: ["mine"],
    queryFn: () => api.myPosts(200),
    enabled: config.hasBase
  });
  const [selected, setSelected] = useState(null);

  if (!config.hasBase) {
    return <EmptyState title="Backend not configured" description="Configure VITE_API_BASE / VITE_API_TOKEN." />;
  }
  if (isLoading) return <div className="subtle">Loading…</div>;
  if (error) return <EmptyState title="Failed to load" description={String(error.message)} />;

  const posts = data?.posts || [];

  return (
    <>
      <PageHeader title="My Posts" subtitle={`${posts.length} captured`} />
      {posts.length === 0 ? (
        <EmptyState title="No posts captured yet" description="Open your LinkedIn profile with the extension installed to backfill." />
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="card max-h-[70vh] overflow-y-auto">
            <ul className="divide-y divide-white/5">
              {posts.map((p) => (
                <li key={p.urn} className="py-3 cursor-pointer" onClick={() => setSelected(p)}>
                  <div className="line-clamp-3 text-sm">{p.body}</div>
                  <div className="flex gap-3 subtle text-xs mt-1">
                    <span>{p.like_count} likes</span>
                    <span>{p.comment_count} comments</span>
                    <span>{p.repost_count} reposts</span>
                    {p.posted_at && <span>{new Date(p.posted_at).toLocaleDateString()}</span>}
                  </div>
                </li>
              ))}
            </ul>
          </div>
          <div className="card">
            {selected ? <PostHistory urn={selected.urn} body={selected.body} /> : <div className="subtle">Click a post to see its engagement timeline.</div>}
          </div>
        </div>
      )}
    </>
  );
}

function PostHistory({ urn, body }) {
  const { data, isLoading } = useQuery({
    queryKey: ["history", urn],
    queryFn: () => api.postHistory(urn)
  });
  return (
    <>
      <div className="text-sm mb-3 line-clamp-3">{body}</div>
      <div className="h-56">
        {isLoading ? (
          <div className="subtle">Loading history…</div>
        ) : data?.history?.length ? (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data.history}>
              <CartesianGrid stroke="#1f2630" strokeDasharray="3 3" />
              <XAxis dataKey="snapshot_at" tickFormatter={(s) => new Date(s).toLocaleDateString()} stroke="#6b7280" />
              <YAxis stroke="#6b7280" />
              <Tooltip contentStyle={{ background: "#0e1119", border: "1px solid #1f2630" }} />
              <Line type="monotone" dataKey="like_count" stroke="#0a66c2" strokeWidth={2} dot={false} name="likes" />
              <Line type="monotone" dataKey="comment_count" stroke="#34d399" strokeWidth={2} dot={false} name="comments" />
              <Line type="monotone" dataKey="repost_count" stroke="#f59e0b" strokeWidth={2} dot={false} name="reposts" />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <div className="subtle text-sm">Need at least two snapshots to see a timeline. Revisit this post on LinkedIn.</div>
        )}
      </div>
    </>
  );
}
